// Real Firebase Templates Service

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

export interface EventTemplate {
  id: string;
  djId: string;
  name: string;
  settings: {
    blockExplicit: boolean;
    profanityFilter: boolean;
    requireApproval: boolean;
    warnExplicit: boolean;
    votingEnabled: boolean;
    requestsOpen: boolean;
    maxRequestsPerUser: number;
    postCloseRequestLimit: number;
    autoApprove: boolean;
    cooldownMinutes: number;
    votingClosed: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

function docToTemplate(docSnap: any): EventTemplate {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    djId: data.djId,
    name: data.name,
    settings: data.settings,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  };
}

export function getAllTemplates(): EventTemplate[] {
  // Not used in practice — kept for interface compat
  return [];
}

export async function getTemplatesByDjId(djId: string): Promise<EventTemplate[]> {
  const q = query(collection(db, 'eventTemplates'), where('djId', '==', djId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToTemplate);
}

export async function getTemplateById(templateId: string): Promise<EventTemplate | null> {
  const docSnap = await getDoc(doc(db, 'eventTemplates', templateId));
  if (!docSnap.exists()) return null;
  return docToTemplate(docSnap);
}

export async function createTemplate(
  djId: string,
  data: { name: string; settings: EventTemplate['settings'] }
): Promise<EventTemplate> {
  const docRef = await addDoc(collection(db, 'eventTemplates'), {
    djId,
    name: data.name,
    settings: data.settings,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const now = new Date().toISOString();
  return { id: docRef.id, djId, name: data.name, settings: data.settings, createdAt: now, updatedAt: now };
}

export async function updateTemplate(
  templateId: string,
  data: Partial<Omit<EventTemplate, 'id' | 'djId' | 'createdAt'>>
): Promise<EventTemplate> {
  const ref = doc(db, 'eventTemplates', templateId);
  await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
  const updated = await getDoc(ref);
  return docToTemplate(updated);
}

export async function deleteTemplate(templateId: string): Promise<void> {
  await deleteDoc(doc(db, 'eventTemplates', templateId));
}

export async function deleteTemplatesByDjId(djId: string): Promise<void> {
  const q = query(collection(db, 'eventTemplates'), where('djId', '==', djId));
  const snapshot = await getDocs(q);
  const batch = writeBatch(db);
  snapshot.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
}

export function getTemplatesStorageKey(): string {
  return 'eventTemplates';
}
